import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Play, Pause, Square, Clock as ClockIcon } from "lucide-react";
import { toast } from "sonner";

function fmt(secs) {
  const h = Math.floor(secs/3600).toString().padStart(2,"0");
  const m = Math.floor((secs%3600)/60).toString().padStart(2,"0");
  const s = Math.floor(secs%60).toString().padStart(2,"0");
  return `${h}:${m}:${s}`;
}
function elapsed(e) {
  const start = new Date(e.start_at).getTime();
  const end = e.end_at ? new Date(e.end_at).getTime() : Date.now();
  let total = (end - start)/1000 - (e.pause_total_sec||0);
  if (e.status === "paused" && e.paused_at) total -= (Date.now() - new Date(e.paused_at).getTime())/1000;
  return Math.max(0, total);
}

export default function Time() {
  const [entries, setEntries] = useState([]);
  const [tick, setTick] = useState(0);
  const [jobs, setJobs] = useState([]);

  async function load() {
    const [e, j] = await Promise.all([api.get("/time-entries"), api.get("/jobs").catch(()=>({data:[]}))]);
    setEntries(e.data); setJobs(j.data);
  }
  useEffect(()=>{ load(); }, []);
  useEffect(()=>{ const i = setInterval(()=>setTick(t=>t+1), 1000); return ()=>clearInterval(i); }, []);

  const running = entries.find(e => e.status === "running" || e.status === "paused");

  async function start() { await api.post("/time-entries/start", { note: "" }); toast.success("Zeiterfassung gestartet"); load(); }
  async function pause() { await api.post(`/time-entries/${running.id}/pause`); load(); }
  async function stop() { await api.post(`/time-entries/${running.id}/stop`); toast.success("Gestoppt"); load(); }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="time-page">
      <div>
        <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Arbeitszeit</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Zeiterfassung</h1>
      </div>

      <Card className="rounded-3xl p-8 border-border/60 glass text-center" data-testid="time-tracker">
        <div className="w-20 h-20 mx-auto rounded-3xl bg-primary/15 text-primary flex items-center justify-center mb-4"><ClockIcon className="w-8 h-8" /></div>
        <div className="font-mono text-6xl font-heading font-bold tracking-tight" data-testid="time-display">
          {running ? fmt(elapsed(running)) : "00:00:00"}
        </div>
        <div className="text-sm text-muted-foreground mt-2">{running ? (running.status === "running" ? "Läuft" : "Pausiert") : "Bereit zu starten"}</div>
        <div className="flex items-center justify-center gap-3 mt-6">
          {!running && <Button onClick={start} size="lg" className="rounded-full px-8 bg-accent hover:bg-accent/90" data-testid="btn-start"><Play className="w-5 h-5 mr-2" />Start</Button>}
          {running && <Button onClick={pause} size="lg" variant="secondary" className="rounded-full px-8" data-testid="btn-pause"><Pause className="w-5 h-5 mr-2" />{running.status === "paused" ? "Fortfahren" : "Pause"}</Button>}
          {running && <Button onClick={stop} size="lg" variant="destructive" className="rounded-full px-8" data-testid="btn-stop"><Square className="w-5 h-5 mr-2" />Stop</Button>}
        </div>
      </Card>

      <div>
        <h3 className="font-heading text-xl font-semibold mb-4">Verlauf</h3>
        <div className="space-y-2">
          {entries.filter(e=>e.status==="stopped").map(e => (
            <Card key={e.id} className="rounded-2xl p-4 border-border/60 flex items-center justify-between" data-testid={`entry-${e.id}`}>
              <div>
                <div className="font-medium">{new Date(e.start_at).toLocaleString('de-DE')}</div>
                <div className="text-xs text-muted-foreground">{jobs.find(j=>j.id===e.job_id)?.title || "Allgemein"}</div>
              </div>
              <Badge className="rounded-full bg-primary/15 text-primary font-mono">{fmt(elapsed(e))}</Badge>
            </Card>
          ))}
          {entries.filter(e=>e.status==="stopped").length === 0 && <div className="text-sm text-muted-foreground text-center py-8">Noch keine Einträge.</div>}
        </div>
      </div>
    </div>
  );
}
